import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { matchPath } from 'react-router'

import { useHasTripItems } from '../api/tripItem'

import { tripSavedSnackbarEvent } from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import LineHeartIcon from 'LuxKit/Icons/line/LineHeartIcon'
import SolidHeartIcon from 'LuxKit/Icons/solid/SolidHeartIcon'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import ModalContext from 'contexts/ModalContext'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { useAppDispatch } from 'hooks/reduxHooks'
import noop from 'lib/function/noop'
import SelectDifferentTrip from 'tripPlanner/components/Bookmark/Common/SelectDifferentTrip'
import { setFirstTripItemHasJustBeenAdded } from 'tripPlanner/reducers/actions'
import type { ItemType } from 'tripPlanner/types/tripItem'
import { getTripURL } from 'tripPlanner/utils'

type SnackbarAction = Exclude<
  Parameters<typeof showSnackbar>[2],
  undefined
>['action']

export default function useBookmarkSnackbarHandlers() {
  const intl = useIntl()
  const VIEW_LABEL = intl.formatMessage({
    id: 'EK83oQcLQbiKHd9QibqL',
    defaultMessage: 'View',
    description: 'Snackbar action label to view the trip after saving or removing an item',
  })
  const CHANGE_LABEL = intl.formatMessage({
    id: 'G0FK_4zC96HQSeAMBixI',
    defaultMessage: 'Change',
    description: 'Snackbar action label to change which trip an item is saved to',
  })

  const isAlreadyOnTrip = useMemo(
    () =>
      !!matchPath('/trip/:tripId/:tab', {
        exact: false,
      }),
    [],
  )

  const showModal = useContext(ModalContext)
  const globalSearchState = useContext(GlobalSearchStateContext)
  const offerPageState = useContext(OfferPageStateContext)

  const showRemoveSuccessSnackbar = useCallback(
    (tripId: string, tripName: string, onViewClick = noop) => {
      showSnackbar(intl.formatMessage(
        {
          id: '-16oSaUBZOj719bFxDhZ',
          defaultMessage: 'Removed from "{tripName}"',
          description: 'Success snackbar message after removing a saved item from a trip',
        },
        { tripName },
      ), 'neutral', {
        action: !isAlreadyOnTrip ?
            {
              label: VIEW_LABEL,
              to: getTripURL(tripId),
              onAction: () => {
                onViewClick()
              },
            } :
          undefined,
        icon: <LineHeartIcon colour="normal-contrast" />,
      })
    },
    [intl, isAlreadyOnTrip, VIEW_LABEL],
  )

  const showRemoveErrorSnackbar = useCallback(
    (tripName: string) =>
      showSnackbar(intl.formatMessage(
        {
          id: 'wWezo-Pr1pVKL-_1Q40i',
          defaultMessage: 'Failed to remove from "{tripName}"',
          description: 'Error snackbar message when removing a saved item from a trip fails',
        },
        { tripName },
      ), 'critical'),
    [intl],
  )
  const hasTripItems = useHasTripItems()
  const dispatch = useAppDispatch()
  const showSaveSuccessSnackbar = useCallback(
    (
      tripId: string,
      tripName: string,
      itemId: string,
      itemType?: ItemType,
      didAutoSelectTrip = false,
      onViewClick = noop,
    ) => {
      if (hasTripItems) {
        let action: SnackbarAction | undefined
        let analyticsAction: Parameters<typeof tripSavedSnackbarEvent>[1]

        if (isAlreadyOnTrip) {
          analyticsAction = 'no_action'
          action = undefined
        } else if (didAutoSelectTrip && itemType) {
          analyticsAction = 'change'
          action = {
            label: CHANGE_LABEL,
            onAction: () => {
              showModal(
                // Need to forward the contexts and modals don't have access
                // We need access in the trip settings modal
                <GlobalSearchStateContext.Provider value={globalSearchState}>
                  <OfferPageStateContext.Provider value={offerPageState}>
                    <SelectDifferentTrip
                      tripId={tripId}
                      tripItemId={itemId}
                      tripItemType={itemType}
                    />
                  </OfferPageStateContext.Provider>
                </GlobalSearchStateContext.Provider>,
              )
              fireInteractionEvent(
                tripSavedSnackbarEvent('click', analyticsAction),
              )
            },
          }
        } else {
          analyticsAction = 'view'
          action = {
            label: VIEW_LABEL,
            to: getTripURL(tripId, itemId),
            'data-testid': 'save-success-snackbar-trip-link',
            onAction: () => {
              fireInteractionEvent(
                tripSavedSnackbarEvent('click', analyticsAction),
              )
              onViewClick()
            },
          }
        }
        fireInteractionEvent(tripSavedSnackbarEvent('view', analyticsAction))
        showSnackbar(intl.formatMessage(
          {
            id: 'ZdwHI4XNi1rQo-F7sSMB',
            defaultMessage: 'Saved to "{tripName}"',
            description: 'Success snackbar message after saving an item to a trip',
          },
          { tripName },
        ), 'neutral', {
          action,
          icon: <SolidHeartIcon colour="favourite" />,
        })
      } else {
        dispatch(setFirstTripItemHasJustBeenAdded())
      }
    },
    [
      intl,
      dispatch,
      globalSearchState,
      hasTripItems,
      isAlreadyOnTrip,
      offerPageState,
      showModal,
      VIEW_LABEL,
      CHANGE_LABEL,
    ],
  )

  const showSaveErrorSnackbar = useCallback(
    (tripName?: string) =>
      showSnackbar(
        tripName
          ? intl.formatMessage(
              {
                id: 'Rl8OgK5jIvnd_3_udkjE',
                defaultMessage: 'Failed to save to "{tripName}"',
                description: 'Error snackbar message when saving an item to a specific trip fails',
              },
              { tripName },
            )
          : intl.formatMessage({
              id: '9qAjNeD93CWYNpUZp3ps',
              defaultMessage: 'Failed to save item',
              description: 'Error snackbar message when saving an item fails and no trip name is available',
            }),
        'critical',
      ),
    [intl],
  )

  return {
    showRemoveSuccessSnackbar,
    showRemoveErrorSnackbar,
    showSaveSuccessSnackbar,
    showSaveErrorSnackbar,
  }
}
